import get from 'lodash/get';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import produce, { enableAllPlugins } from 'immer';
import type { TranslatedErrors } from '../validator/joi-translator/joi-type';
import { getTranslatedErrors } from '../validator/joi-translator/joi-translator';
import {
  ACTION_OPEN_METADATA_PICKER,
  ACTION_CLOSE_METADATA_PICKER,
  ACTION_SELECT_METADATA_TEMPLATE,
  ACTION_OPEN_FIELD_SELECTOR_DRAWER,
  ACTION_INSERT_FIELD,
  ACTION_CLOSE_FIELD_SELECTOR_DRAWER,
  ACTION_SHOW_METADATA_FIELDS,
  ACTION_HIDE_METADATA_FIELDS,
  ACTION_RESPONSE_ERROR_ON_SAVE,
  ACTION_OPEN_CLEAR_METADATA_TEMPLATE_MODAL,
  ACTION_CLOSE_CLEAR_METADATA_TEMPLATE_MODAL,
  ACTION_CLEAR_METADATA_TEMPLATE,
  ACTION_ADD_UPLOAD_FIELD_TO_METADATA_FIELD,
  ACTION_REMOVE_UPLOAD_FIELD_FROM_METADATA_FIELD,
  ACTION_ADD_FIELD,
  ACTION_ADD_METADATA_FIELD,
  ACTION_ADD_EMAIL_FIELD,
  ACTION_ADD_DESCRIPTION_FIELD,
  ACTION_REMOVE_FIELD,
  ACTION_TOGGLE_REQUIRED_FIELD,
  ACTION_ADD_UPLOAD_FIELD,
  ACTION_OPEN_DELETE_FILE_REQUEST_MODAL,
  ACTION_CLOSE_DELETE_FILE_REQUEST_MODAL,
  ACTION_ON_FOCUS_ELEMENT,
  ACTION_ON_UNFOCUS_ELEMENT,
  ACTION_CLOSE_NOTIFICATION,
  ACTION_SAVE_IN_PROGRESS,
  ACTION_SAVE_BEFORE_SHARE_IN_PROGRESS,
  ACTION_SAVE_BEFORE_PREVIEW_IN_PROGRESS,
  ACTION_RESET_SAVE_BEFORE_PREVIEW_IN_PROGRESS,
  ACTION_SAVE_COMPLETED,
  ACTION_MISMATCHED_VERSION,
  ACTION_REORDER_GROUP_FIELDS,
  ACTION_OPEN_SHARE_MODAL,
  ACTION_CREATE_FORM_AND_SHARE,
  ACTION_FILE_REQUEST_UPDATED_FROM_SHARE_MODAL,
  ACTION_CLOSE_SHARE_MODAL,
  ACTION_SET_LABEL,
  ACTION_UPDATE_LIST_OPTIONS,
  ACTION_UPDATE_MAXIMUM_SELECTIONS,
  ACTION_SET_DESCRIPTION,
  ACTION_SET_DESCRIPTION_EDITABLE,
  ACTION_SET_LOGIC_ERRORS,
  ACTION_DELETE_LOGIC_ERRORS,
  ACTION_REMOVE_DESCRIPTION,
  ACTION_OPEN_EXIT_MODAL,
  ACTION_CLOSE_EXIT_MODAL,
  ACTION_DRAGGING_STARTED,
  ACTION_DRAGGING_FINISHED,
  ACTION_HAS_SCROLLED_TO_BOTTOM,
  // Action associated with copy file request
  ACTION_OPEN_COPY_MODAL,
  ACTION_CLOSE_COPY_MODAL,
  ACTION_SEARCH_ITEMS,
  ACTION_SEARCH_ITEMS_IN_PROGRESS,
  ACTION_GET_FOLDER_TREE,
  ACTION_GET_FOLDER_TREE_IN_PROGRESS,
  ACTION_CLEAR_CONTENT_PICKER,
  ACTION_COPY_FILE_REQUEST_IN_PROGRESS,
  ACTION_COPY_FILE_REQUEST,
  // Actions associated with custom branding
  ACTION_OPEN_UPLOAD_LOGO_MODAL,
  ACTION_CLOSE_UPLOAD_LOGO_MODAL,
  ACTION_OPEN_THEME_PANEL,
  ACTION_CLOSE_THEME_PANEL,
  ACTION_UPDATE_UPLOAD_LOGO_ITEM,
  ACTION_SET_UPLOADED_LOGO,
  ACTION_UPLOAD_LOGO_TOO_LARGE,
  ACTION_UPLOAD_LOGO_IS_NOT_IMAGE,
  ACTION_UPLOAD_LOGO_IN_PROGRESS,
  ACTION_UPLOAD_LOGO,
  ACTION_UPLOAD_LOGO_CLEAR,
  ACTION_UPLOAD_LOGO_ERROR,
  ACTION_UPLOAD_LOGO_EMPTY,
  ACTION_HEADER_LOGO_SELECT,
  ACTION_SELECT_THEME_COLOR,
  ACTION_SHOW_COLOR_PICKER,
  ACTION_COLOR_PICKER_CANCEL,
  ACTION_COLOR_PICKER_CHANGE,
  ACTION_COLOR_PICKER_ADD,
  ACTION_COLOR_PICKER_ERROR,
  defaultContentPickerState,
  defaultState,
  ACTION_TOGGLE_FILE_REQUEST_ACTIVITY,
  ACTION_NOTIFY_FILE_REQUEST_INACTIVE,
  ACTION_NOTIFY_METADATA_TEMPLATE_ERROR,
  ACTION_NOTIFY_UNSUPPORTED_CHARACTERS_ERROR,
  ACTION_DEACTIVATE_BAD_FILE_REQUEST,
  ACTION_DRAG_FORM_FIELD_START,
  ACTION_DRAG_FORM_FIELD_END,
  ACTION_DRAG_FORM_FIELD_ENTER_DROPZONE,
  ACTION_DRAG_FORM_FIELD_LEFT_DROPZONE,
  ACTION_INSERT_METADATA,
  ACTION_SET_FORM_ELEMENT_TESTIDS,
  ACTION_STORE_INSERT_INDEX,
  ACTION_UPDATE_GROUP_LAYOUT,
  ACTION_STORE_FORM_ELEMENT_HEIGHT,
  ACTION_TOOLBAR_DROPDOWN_OPEN,
  ACTION_TOOLBAR_DROPDOWN_CLOSE,
  ACTION_UPDATE_FORM_LOGIC,
  ACTION_OPEN_SETTINGS_PANEL,
  ACTION_CLOSE_SETTINGS_PANEL,
  ACTION_DUPLICATE_FIELD,
  ACTION_TOGGLE_SHOW_UPLOAD_FILE_DESCRIPTION,
  ACTION_OPEN_SELECT_MODAL,
  ACTION_CLOSE_SELECT_MODAL,
  FOLDER_PICKER_SELECT_MODE,
  FOLDER_PICKER_COPY_MODE,
  ACTION_SELECT_FORM_UPLOAD_FOLDER,
  ACTION_CLEAR_FORM_UPLOAD_FOLDER,
  ACTION_VALIDATION_ERROR_ON_SAVE,
  ACTION_SET_BUILDER_ERROR,
  ACTION_INJECT_JOI_VALIDATION_ERRORS,
  ACTION_CLEAR_LOGIC_ERRORS,
  ACTION_SET_LIST_OPTIONS_ERROR,
  ACTION_DELETE_LOGIC_ITEM,
  ADMIN_COLOR_LOGO,
  ADMIN_DEFAULT_LOGO,
  ACTION_CLEAR_ALL_LOGIC_ITEMS,
  ACTION_ALREADY_SAVED,
} from '../builder-constant';
import {
  HOMEPAGE_FOLDER_ID,
  FILE_REQUEST_STATUS,
  FOLDER_URL,
  FORM_TYPE_FORM,
} from '../../../../constants';
import selector from '../builder-selector';
import type { State, Action } from '../builder-type';
import messages from '../../../new-file-request-builder/builder-notification/messages';
import uploadMessages from '../../../new-builder-right-panel-content/theme-panel/upload-logo-modal/upload-modal/messages';
import {
  addUploadFieldToMetadataField,
  removeUploadFieldFromMetadataField,
  removeField,
  focusElement,
  blurElement,
  updateListFieldOptions,
  setListOptionsError,
  toggleShowUploadFileDescription,
  updateGroupLayout,
  dragFormFieldStart,
  dragFormFieldEnd,
  setDragFormFieldInDropzone,
  setDropIndex,
  setFormElementTestIds,
  storeFormElementHeight,
  openUploadLogoModal,
  closeUploadLogoModal,
  updateUploadLogoModalItem,
  setUploadLogoModalErrorMessage,
  uploadLogoRequestInProgress,
  uploadLogoRequestOnError,
  uploadLogoRequestOnSuccess,
  setUploadedLogo,
  clearUploadedLogo,
  setThemeHeaderLogo,
  selectThemeColor,
  showColorPicker,
  colorPickerCancel,
  colorPickerChange,
  colorPickerSave,
  colorPickerOnError,
  setIsThemePanelOpen,
  addEmailField,
  addFileDescriptionField,
  toggleFieldRequired,
  addField,
  addUploadField,
  openElementToolbar,
  closeElementToolbar,
  setFieldLabel,
  setFieldMaximumSelections,
  setFieldDescription,
  setEditableDescriptions,
  removeFieldDescription,
  closeShareModal,
  openShareModal,
  fileRequestUpdatedFromShareModal,
  setIsExitModalOpen,
  setIsDeleteFileRequestModalOpen,
  draggingStarted,
  draggingFinished,
  hasScrolledToBottom,
  setFormContent,
  reorderGroupFields,
  insertMetadataField,
} from './builder-reducer-handlers';
import {
  validateFormBuilderFields,
  injectJOIErrorsToFormBuilderFields,
} from '../validator/builder-validator';

// Enable all immer plugins
enableAllPlugins();

const setErrorMessageOnMismatch = (draftState: State) => {
  if (draftState.workingFileRequest.type === FORM_TYPE_FORM) {
    draftState.errorMessage = messages.errorMismatchedNotificationForms;
  } else {
    draftState.errorMessage = messages.errorMismatchedNotification;
  }
};

const setErrorMessageWhenUnsupportedCharacters = (draftState: State) => {
  if (draftState.workingFileRequest.type === FORM_TYPE_FORM) {
    draftState.errorMessage =
      messages.errorUnsupportedCharactersNotificationForms;
  } else {
    draftState.errorMessage =
      messages.errorUnsupportedCharactersNotificationFileRequest;
  }
};

export default (state: State, action: Action): State => {
  switch (action.type) {
    case ACTION_OPEN_METADATA_PICKER:
      return produce(state, (draftState: State) => {
        draftState.isMetadataPickerOpen = true;
      });
    case ACTION_CLOSE_METADATA_PICKER:
      return produce(state, (draftState: State) => {
        draftState.isMetadataPickerOpen = false;
      });
    case ACTION_SELECT_METADATA_TEMPLATE: {
      const { newMetadataTemplates, formContent } = action;

      return produce(state, (draftState: State) => {
        const allMetadataFieldsSelected =
          selector.getUnselectedMetadataFields(draftState).length === 0;
        draftState.workingFileRequest.form.content = formContent;
        draftState.activeMetadataTemplates = newMetadataTemplates;
        draftState.isMetadataPickerOpen = false;
        draftState.isMetadataFieldShown = false;
        draftState.isFieldSelectorDrawerOpen = false;
        draftState.allMetadataFieldsSelected = allMetadataFieldsSelected;
        draftState.needToScrollToBottom = true;
      });
    }
    case ACTION_OPEN_FIELD_SELECTOR_DRAWER:
      return produce(state, (draftState: State) => {
        draftState.elementInFocus = '';
        draftState.isFieldSelectorDrawerOpen = true;
        draftState.needToScrollToBottom = true;
      });
    case ACTION_CLOSE_FIELD_SELECTOR_DRAWER:
      return produce(state, (draftState: State) => {
        draftState.isFieldSelectorDrawerOpen = false;
        draftState.isMetadataFieldShown = false;
      });
    case ACTION_SHOW_METADATA_FIELDS:
      return produce(state, (draftState: State) => {
        draftState.isMetadataFieldShown = true;
      });
    case ACTION_HIDE_METADATA_FIELDS:
      return produce(state, (draftState: State) => {
        draftState.isMetadataFieldShown = false;
      });
    case ACTION_OPEN_CLEAR_METADATA_TEMPLATE_MODAL:
      return produce(state, (draftState: State) => {
        draftState.isClearMetadataTemplateModalOpen = true;
      });
    case ACTION_CLOSE_CLEAR_METADATA_TEMPLATE_MODAL:
      return produce(state, (draftState: State) => {
        draftState.isClearMetadataTemplateModalOpen = false;
      });
    case ACTION_CLEAR_METADATA_TEMPLATE: {
      const { formContent } = action;

      return produce(state, (draftState: State) => {
        draftState.workingFileRequest.form.content = formContent;
        draftState.activeMetadataTemplates = [];
        draftState.metadataTemplateError = false;
        draftState.allMetadataFieldsSelected = false;
        draftState.isMetadataFieldShown = false;
        draftState.isClearMetadataTemplateModalOpen = false;
      });
    }
    // TODO: Figure out where this is being used? this was missed by the update to support multiple metadata templates
    // Update: I don't think this is being used anymore, I'm not going to add a taxonomy field support in this action
    // ACTION_INSERT_METADATA is used instead and this should be deprecated
    case ACTION_ADD_METADATA_FIELD: {
      const { newMetadataTemplate } = action;

      return produce(state, (draftState: State) => {
        const allMetadataFieldsSelected =
          selector.getUnselectedMetadataFields(draftState).length === 0;
        if (!draftState.activeMetadataTemplates.includes(newMetadataTemplate)) {
          draftState.activeMetadataTemplates.push(newMetadataTemplate);
        }
        draftState.isMetadataFieldShown = false;
        draftState.allMetadataFieldsSelected = allMetadataFieldsSelected;
        draftState.needToScrollToBottom = true;
      });
    }
    case ACTION_ADD_UPLOAD_FIELD_TO_METADATA_FIELD: {
      return addUploadFieldToMetadataField(state, action);
    }
    case ACTION_REMOVE_UPLOAD_FIELD_FROM_METADATA_FIELD: {
      return removeUploadFieldFromMetadataField(state, action);
    }
    case ACTION_ADD_FIELD: {
      return addField(state, action);
    }
    case ACTION_ADD_UPLOAD_FIELD: {
      return addUploadField(state);
    }
    case ACTION_ADD_EMAIL_FIELD: {
      return addEmailField(state);
    }
    case ACTION_ADD_DESCRIPTION_FIELD: {
      return addFileDescriptionField(state);
    }
    case ACTION_REMOVE_FIELD: {
      return removeField(state, action);
    }
    case ACTION_TOGGLE_REQUIRED_FIELD: {
      return toggleFieldRequired(state, action);
    }
    case ACTION_TOOLBAR_DROPDOWN_OPEN: {
      return openElementToolbar(state, action.id);
    }
    case ACTION_TOOLBAR_DROPDOWN_CLOSE: {
      return closeElementToolbar(state, action.id);
    }
    case ACTION_ON_FOCUS_ELEMENT: {
      return focusElement(state, action);
    }
    case ACTION_ON_UNFOCUS_ELEMENT: {
      return blurElement(state, action);
    }
    case ACTION_SET_LABEL: {
      return setFieldLabel(state, action);
    }
    case ACTION_TOGGLE_SHOW_UPLOAD_FILE_DESCRIPTION: {
      return toggleShowUploadFileDescription(state, action);
    }
    case ACTION_UPDATE_MAXIMUM_SELECTIONS: {
      return setFieldMaximumSelections(state, action);
    }
    case ACTION_UPDATE_LIST_OPTIONS: {
      return updateListFieldOptions(state, action);
    }
    case ACTION_SET_LIST_OPTIONS_ERROR: {
      return setListOptionsError(state, action);
    }
    case ACTION_SET_DESCRIPTION: {
      return setFieldDescription(state, action);
    }
    case ACTION_SET_DESCRIPTION_EDITABLE: {
      return setEditableDescriptions(state, action);
    }
    case ACTION_REMOVE_DESCRIPTION: {
      return removeFieldDescription(state, action);
    }
    case ACTION_CLOSE_NOTIFICATION:
      return produce(state, (draftState: State) => {
        draftState.successMessage = null;
        draftState.warnMessage = null;
        draftState.errorMessage = null;
        draftState.notificationOptions = defaultState.notificationOptions;
      });
    case ACTION_TOGGLE_FILE_REQUEST_ACTIVITY: {
      const { fileRequest } = action;

      return produce(state, (draftState: State) => {
        draftState.workingFileRequest = cloneDeep(fileRequest);
        draftState.savedFileRequest = cloneDeep(fileRequest);
        draftState.deactivatedBadFileRequest =
          fileRequest.status === FILE_REQUEST_STATUS.INACTIVE &&
          draftState.deactivatedBadFileRequest;
      });
    }
    case ACTION_DEACTIVATE_BAD_FILE_REQUEST:
      return produce(state, (draftState: State) => {
        draftState.deactivatedBadFileRequest = true;
      });
    case ACTION_NOTIFY_METADATA_TEMPLATE_ERROR:
      return produce(state, (draftState: State) => {
        draftState.errorMessage = messages.errorMetadataTemplateNotification;
        draftState.saveInProgress = false;
        draftState.saveBeforePreviewInProgress = false;
        draftState.notificationOptions = {
          duration: '', // No timeout, notification will display until acted upon
          onCloseAction: ACTION_NOTIFY_FILE_REQUEST_INACTIVE,
          onMessageAction: {
            closeOnClick: true,
            messages: messages.clearMetadataTemplateButton,
            onClick: ACTION_CLEAR_METADATA_TEMPLATE,
          },
        };
      });
    case ACTION_NOTIFY_UNSUPPORTED_CHARACTERS_ERROR:
      return produce(state, (draftState: State) => {
        draftState.saveInProgress = false;
        draftState.saveBeforeShareInProgress = false;
        draftState.saveBeforePreviewInProgress = false;
        setErrorMessageWhenUnsupportedCharacters(draftState);
      });
    case ACTION_NOTIFY_FILE_REQUEST_INACTIVE:
      return produce(state, (draftState: State) => {
        draftState.warnMessage = {
          ...messages.inactiveNotification,
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2353
          values: { type: draftState.workingFileRequest.type },
        };
        draftState.notificationOptions = defaultState.notificationOptions;
      });
    case ACTION_SAVE_BEFORE_PREVIEW_IN_PROGRESS: {
      return produce(state, (draftState: State) => {
        draftState.saveBeforePreviewInProgress = true;
      });
    }
    case ACTION_RESET_SAVE_BEFORE_PREVIEW_IN_PROGRESS: {
      return produce(state, (draftState: State) => {
        draftState.saveBeforePreviewInProgress = false;
      });
    }
    case ACTION_SAVE_BEFORE_SHARE_IN_PROGRESS: {
      return produce(state, (draftState: State) => {
        draftState.saveBeforeShareInProgress = true;
      });
    }
    case ACTION_SAVE_IN_PROGRESS: {
      return produce(state, (draftState: State) => {
        draftState.saveInProgress = true;
      });
    }
    case ACTION_SAVE_COMPLETED: {
      const { fileRequest } = action;
      return produce(state, (draftState: State) => {
        draftState.workingFileRequest = cloneDeep(fileRequest);
        draftState.savedFileRequest = cloneDeep(fileRequest);
        draftState.saveInProgress = false;
        draftState.saveBeforeShareInProgress = false;
        draftState.saveBeforePreviewInProgress = false;
        draftState.successMessage = messages.successSaveNotification;
        /* So that we reset the staged logo. This is for Forms, has no effect on FR */
        draftState.themePanelState.stagedLogoPath = '';

        // Update uploadedLogo in theme panel with the result of the save
        // If the logo was just uploaded, we'd be replacing a local image on the user's device with the picPath and url from Box Pic Storage
        // If an uploadedLogo was replaced by an admin logo or with "no logo", then it would get removed from the theme panel.
        const logo = fileRequest?.form?.content?.theme?.logo;
        const uploadedLogo = draftState.themePanelState?.logos?.uploadedLogo;

        if (
          uploadedLogo?.key !== logo?.key ||
          uploadedLogo?.value !== logo?.value
        ) {
          if ([ADMIN_COLOR_LOGO, ADMIN_DEFAULT_LOGO].includes(logo?.key)) {
            draftState.themePanelState.logos.uploadedLogo.key = null;
            draftState.themePanelState.logos.uploadedLogo.value = null;
          } else {
            draftState.themePanelState.logos.uploadedLogo.key = logo?.key;
            draftState.themePanelState.logos.uploadedLogo.value = logo?.value;
          }
        }
      });
    }
    case ACTION_ALREADY_SAVED: {
      return produce(state, (draftState: State) => {
        draftState.successMessage = messages.successAlreadySavedNotification;
      });
    }
    case ACTION_VALIDATION_ERROR_ON_SAVE: {
      const { folderInfoById } = action;
      return produce(state, (draftState: State) => {
        draftState.saveInProgress = false;
        draftState.saveBeforeShareInProgress = false;

        // merge in folder info returned by the validation error
        draftState.workingFileRequest.folderInfoById = {
          ...draftState.workingFileRequest.folderInfoById,
          ...folderInfoById,
        };
        // Add error messages to upload fields that are invalid
        validateFormBuilderFields(draftState);
      });
    }
    case ACTION_RESPONSE_ERROR_ON_SAVE: {
      return produce(state, (draftState: State) => {
        draftState.saveInProgress = false;
        draftState.saveBeforeShareInProgress = false;

        draftState.errorMessage = {
          ...messages.unknownOnSaveError,
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2353
          values: { type: draftState.workingFileRequest.type },
        };
      });
    }
    case ACTION_MISMATCHED_VERSION: {
      const { fileRequest } = action;
      return produce(state, (draftState: State) => {
        draftState.workingFileRequest = cloneDeep(fileRequest);
        draftState.savedFileRequest = cloneDeep(fileRequest);
        draftState.saveInProgress = false;
        draftState.saveBeforeShareInProgress = false;
        draftState.saveBeforePreviewInProgress = false;
        setErrorMessageOnMismatch(draftState);
      });
    }
    case ACTION_OPEN_SHARE_MODAL:
    case ACTION_CREATE_FORM_AND_SHARE: {
      return openShareModal(state);
    }
    case ACTION_FILE_REQUEST_UPDATED_FROM_SHARE_MODAL: {
      return fileRequestUpdatedFromShareModal(state, action);
    }
    case ACTION_CLOSE_SHARE_MODAL: {
      return closeShareModal(state, action);
    }
    case ACTION_OPEN_EXIT_MODAL: {
      return setIsExitModalOpen(state, true);
    }
    case ACTION_CLOSE_EXIT_MODAL: {
      return setIsExitModalOpen(state, false);
    }
    case ACTION_OPEN_DELETE_FILE_REQUEST_MODAL: {
      return setIsDeleteFileRequestModalOpen(state, true);
    }
    case ACTION_CLOSE_DELETE_FILE_REQUEST_MODAL: {
      return setIsDeleteFileRequestModalOpen(state, false);
    }
    case ACTION_OPEN_COPY_MODAL: {
      return produce(state, (draftState: State) => {
        draftState.isFolderPickerModalOpen = true;
        draftState.folderPickerModalMode = FOLDER_PICKER_COPY_MODE;
      });
    }
    case ACTION_CLOSE_COPY_MODAL: {
      return produce(state, (draftState: State) => {
        draftState.isFolderPickerModalOpen = false;
      });
    }
    case ACTION_OPEN_SELECT_MODAL: {
      const { id } = action;
      return produce(state, (draftState: State) => {
        draftState.isFolderPickerModalOpen = true;
        draftState.folderPickerModalMode = FOLDER_PICKER_SELECT_MODE;
        draftState.folderPickerModalSelectedUploadId = id;
        delete draftState.workingFileRequest.form.content.components[id].error;
      });
    }
    case ACTION_CLOSE_SELECT_MODAL: {
      return produce(state, (draftState: State) => {
        draftState.isFolderPickerModalOpen = false;
        draftState.folderPickerModalSelectedUploadId = '';
      });
    }
    case ACTION_CLEAR_CONTENT_PICKER: {
      return produce(state, (draftState: State) => {
        draftState.contentPickerState = cloneDeep(defaultContentPickerState);
      });
    }
    case ACTION_SEARCH_ITEMS_IN_PROGRESS: {
      return produce(state, (draftState: State) => {
        draftState.contentPickerState.searchInProgress = true;
      });
    }
    case ACTION_SEARCH_ITEMS: {
      const { data } = action;
      const { items } = data;
      return produce(state, (draftState: State) => {
        draftState.contentPickerState.searchInProgress = false;
        draftState.contentPickerState.searchResults = items;
      });
    }
    case ACTION_GET_FOLDER_TREE_IN_PROGRESS: {
      return produce(state, (draftState: State) => {
        draftState.contentPickerState.requestInProgress = true;
      });
    }
    case ACTION_GET_FOLDER_TREE: {
      const { data } = action;
      const { folder, items, pageCount, pageNumber } = data;

      return produce(state, (draftState: State) => {
        draftState.contentPickerState.requestInProgress = false;
        draftState.contentPickerState.items.push(...items);
        const isActionDisabled = folder.id === HOMEPAGE_FOLDER_ID;
        draftState.contentPickerState.folderPath = get(
          folder,
          'path',
          [],
        ).concat({
          id: folder.id,
          name: folder.name,
          isActionDisabled,
          // this is used in the case where someone clicks into a folder and presses select
          isSharedFolder: folder.isCollaborated,
        });
        draftState.contentPickerState.pageCount = pageCount;
        draftState.contentPickerState.pageNumber = pageNumber;
      });
    }
    case ACTION_COPY_FILE_REQUEST_IN_PROGRESS: {
      return produce(state, (draftState: State) => {
        draftState.contentPickerState.onSelectInProgress = true;
      });
    }
    case ACTION_COPY_FILE_REQUEST: {
      const { data, selectionItems } = action;
      const { successes } = data;
      const numberOfFoldersCopied = (successes || []).length;
      const firstFolder =
        find(
          selectionItems,
          item => item.id === get(successes, '[0].folderId', ''),
        ) || {};
      const folderName =
        numberOfFoldersCopied === 1 ? get(firstFolder, 'name') : '';

      return produce(state, (draftState: State) => {
        draftState.isFolderPickerModalOpen = false;
        draftState.contentPickerState = cloneDeep(defaultContentPickerState);
        draftState.successMessage = {
          ...messages.successCopyNotification,
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2353
          values: { count: numberOfFoldersCopied, folderName },
        };
        draftState.notificationOptions = {
          ...defaultState.notificationOptions,
          onMessageAction:
            numberOfFoldersCopied === 1
              ? {
                  onClick: () => {
                    window.location.href = `${FOLDER_URL}${firstFolder.id}`;
                  },
                  messages: messages.showFolderButton,
                }
              : null,
        };
      });
    }
    case ACTION_SELECT_FORM_UPLOAD_FOLDER: {
      const { selectionItem } = action;

      return produce(state, (draftState: State) => {
        draftState.workingFileRequest.form.content.components[
          draftState.folderPickerModalSelectedUploadId
        ].folderId = selectionItem.id;

        draftState.workingFileRequest.folderInfoById =
          draftState.workingFileRequest.folderInfoById || {};

        draftState.workingFileRequest.folderInfoById[selectionItem.id] = {
          canUserAssociateFormUploadFiles:
            selectionItem.canUserAssociateFormUploadFiles,
          isSharedFolder:
            selectionItem.hasCollaborations || selectionItem?.isSharedFolder,
          isExternallyOwned: selectionItem.isExternallyOwned,
          name: selectionItem.name,
        };

        draftState.isFolderPickerModalOpen = false;
      });
    }
    case ACTION_CLEAR_FORM_UPLOAD_FOLDER: {
      const { id } = action;
      return produce(state, (draftState: State) => {
        draftState.workingFileRequest.form.content.components[id].folderId = '';
        delete draftState.workingFileRequest.form.content.components[id].error;
      });
    }
    case ACTION_OPEN_UPLOAD_LOGO_MODAL: {
      return openUploadLogoModal(state);
    }
    case ACTION_CLOSE_UPLOAD_LOGO_MODAL: {
      return closeUploadLogoModal(state);
    }
    case ACTION_UPDATE_UPLOAD_LOGO_ITEM: {
      return updateUploadLogoModalItem(state, action);
    }
    case ACTION_SET_UPLOADED_LOGO: {
      return setUploadedLogo(state, action);
    }
    case ACTION_UPLOAD_LOGO_TOO_LARGE: {
      return setUploadLogoModalErrorMessage(
        state,
        uploadMessages.largeImageError,
      );
    }
    case ACTION_UPLOAD_LOGO_IS_NOT_IMAGE: {
      return setUploadLogoModalErrorMessage(
        state,
        uploadMessages.notImageError,
      );
    }
    case ACTION_UPLOAD_LOGO_IN_PROGRESS: {
      return uploadLogoRequestInProgress(state);
    }
    case ACTION_UPLOAD_LOGO_EMPTY: {
      return uploadLogoRequestOnError(state, uploadMessages.emptyFileError);
    }
    case ACTION_UPLOAD_LOGO_ERROR: {
      return uploadLogoRequestOnError(state, uploadMessages.uploadError);
    }
    case ACTION_UPLOAD_LOGO: {
      return uploadLogoRequestOnSuccess(
        state,
        action,
        uploadMessages.uploadSuccess,
      );
    }
    case ACTION_UPLOAD_LOGO_CLEAR: {
      return clearUploadedLogo(state);
    }
    case ACTION_HEADER_LOGO_SELECT: {
      return setThemeHeaderLogo(state, action.logo);
    }
    case ACTION_SELECT_THEME_COLOR: {
      return selectThemeColor(state, action.color);
    }
    case ACTION_SHOW_COLOR_PICKER: {
      return showColorPicker(state);
    }
    case ACTION_COLOR_PICKER_CANCEL: {
      return colorPickerCancel(state);
    }
    case ACTION_COLOR_PICKER_CHANGE: {
      return colorPickerChange(state, action.color);
    }
    case ACTION_COLOR_PICKER_ADD: {
      return colorPickerSave(state);
    }
    case ACTION_COLOR_PICKER_ERROR: {
      return colorPickerOnError(state, action.errorMessage);
    }
    case ACTION_OPEN_THEME_PANEL: {
      return setIsThemePanelOpen(state, true);
    }
    case ACTION_CLOSE_THEME_PANEL: {
      return setIsThemePanelOpen(state, false);
    }
    case ACTION_DRAG_FORM_FIELD_START: {
      return dragFormFieldStart(state, action);
    }
    case ACTION_DRAG_FORM_FIELD_END: {
      return dragFormFieldEnd(state);
    }
    case ACTION_DRAG_FORM_FIELD_ENTER_DROPZONE: {
      return setDragFormFieldInDropzone(state, true);
    }
    case ACTION_DRAG_FORM_FIELD_LEFT_DROPZONE: {
      return setDragFormFieldInDropzone(state, false);
    }
    case ACTION_STORE_INSERT_INDEX: {
      return setDropIndex(state, action.dropIndex, action.groupId);
    }
    case ACTION_SET_FORM_ELEMENT_TESTIDS: {
      return setFormElementTestIds(state, action);
    }
    case ACTION_STORE_FORM_ELEMENT_HEIGHT: {
      return storeFormElementHeight(state, action);
    }
    case ACTION_UPDATE_GROUP_LAYOUT: {
      return updateGroupLayout(state, action);
    }
    case ACTION_REORDER_GROUP_FIELDS: {
      return reorderGroupFields(state, action);
    }
    case ACTION_DRAGGING_STARTED: {
      return draggingStarted(state, action);
    }
    case ACTION_DRAGGING_FINISHED: {
      return draggingFinished(state, action);
    }
    case ACTION_HAS_SCROLLED_TO_BOTTOM: {
      return hasScrolledToBottom(state);
    }
    case ACTION_DUPLICATE_FIELD:
    case ACTION_INSERT_FIELD: {
      return setFormContent(state, action);
    }
    case ACTION_INSERT_METADATA: {
      return insertMetadataField(state, action);
    }
    case ACTION_UPDATE_FORM_LOGIC: {
      const { newLogic } = action;

      return produce(state, (draftState: State) => {
        draftState.workingFileRequest.form.content.logic = newLogic;
      });
    }
    case ACTION_OPEN_SETTINGS_PANEL: {
      const { fieldId } = action;
      return produce(state, (draftState: State) => {
        draftState.settingsPanelOpen = true;
        draftState.settingsPanelData = { fieldId };
      });
    }
    case ACTION_CLOSE_SETTINGS_PANEL: {
      return produce(state, (draftState: State) => {
        draftState.settingsPanelOpen = false;
      });
    }
    case ACTION_SET_BUILDER_ERROR: {
      return produce(state, (draftState: State) => {
        draftState.errorMessage = action.errorMessage;
      });
    }
    case ACTION_INJECT_JOI_VALIDATION_ERRORS: {
      const { errors, formContent } = action;
      const translatedErrors: TranslatedErrors = getTranslatedErrors(
        errors,
        formContent,
      );
      const { bannerMessage, logicErrors } = translatedErrors;
      return produce(state, (draftState: State) => {
        draftState.saveInProgress = false;
        draftState.saveBeforeShareInProgress = false;
        draftState.errorMessage = bannerMessage;
        // inject error messages into individual form fields state
        injectJOIErrorsToFormBuilderFields(draftState, translatedErrors);
        draftState.logicErrors = logicErrors;
      });
    }
    case ACTION_CLEAR_LOGIC_ERRORS: {
      return produce(state, (draftState: State) => {
        draftState.logicErrors = {};
      });
    }
    case ACTION_SET_LOGIC_ERRORS: {
      const { id } = action;
      return produce(state, (draftState: State) => {
        draftState.logicErrors[id] = true;
      });
    }
    case ACTION_DELETE_LOGIC_ERRORS: {
      const { id } = action;
      return produce(state, (draftState: State) => {
        delete draftState.logicErrors[id];
      });
    }
    case ACTION_DELETE_LOGIC_ITEM: {
      const { id } = action;
      const existingFormLogic = selector.getFormLogic(state);
      return produce(state, (draftState: State) => {
        draftState.workingFileRequest.form.content.logic =
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
          existingFormLogic.filter(logicItem => logicItem.id !== id);
        delete draftState.logicErrors[id];
      });
    }
    case ACTION_CLEAR_ALL_LOGIC_ITEMS: {
      return produce(state, (draftState: State) => {
        delete draftState.workingFileRequest.form.content.logic;
      });
    }
    default:
      return state;
  }
};
